import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles,
  Send,
  CheckCircle2,
  ArrowRight,
  Sprout,
  Instagram,
  Music2,
  Youtube,
  Globe,
  Camera,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";

const PARTNER_TYPES = [
  "Influencer / Creator",
  "Pageant Queen",
  "Personal Trainer",
  "Athlete",
  "Chef",
  "Restaurant",
  "Franchise Restaurant",
  "Wellness Brand",
  "Lifestyle Creator",
  "Other",
];

const COLLAB_INTERESTS = [
  "Gifted product",
  "Paid collaboration",
  "Affiliate commission",
  "Long-term ambassador",
];

export default function BrandPartnerApply() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    city_state: "",
    instagram: "",
    tiktok: "",
    youtube: "",
    website: "",
    partner_type: PARTNER_TYPES[0],
    follower_count: "",
    primary_audience: "",
    why_partner: "",
    prior_brand_experience: "",
    collab_interests: [],
    media_kit_url: "",
    additional_notes: "",
    payout_method: "",
    agreement_accepted: false,
    agreement_signed_name: "",
    photo_url: "",
    title_tagline: "",
    public_bio: "",
    featured_quote: "",
    company: "", hp_token: "", // honeypot
  });
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoErr, setPhotoErr] = useState("");

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoErr("");
    if (!file.type.startsWith("image/")) { setPhotoErr("Please choose an image file."); return; }
    if (file.size > 8 * 1024 * 1024) { setPhotoErr("Image must be under 8 MB."); return; }
    setPhotoUploading(true);
    try {
      const { data } = await api.post("/uploads/presign-public", { content_type: file.type, filename: file.name });
      const fd = new FormData();
      Object.entries(data.fields || {}).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", file);
      const res = await fetch(data.upload_url, { method: "POST", body: fd });
      if (!res.ok) throw new Error(`S3 upload failed (${res.status})`);
      setForm((f) => ({ ...f, photo_url: data.public_url }));
    } catch {
      setPhotoErr("Upload failed. Please try again.");
    } finally { setPhotoUploading(false); }
  };
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInterest = (label) =>
    setForm((f) => ({
      ...f,
      collab_interests: f.collab_interests.includes(label)
        ? f.collab_interests.filter((x) => x !== label)
        : [...f.collab_interests, label],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.photo_url) { setErr("Please upload a profile photo before submitting."); return; }
    setSubmitting(true);
    setErr("");
    try {
      const { data } = await api.post("/partners/brand/apply", form);
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Submission failed. Please try again.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white" data-testid="brand-apply-success">
        <Seo title="Application received" path="/partners/brand" />
        <div
          className="max-w-3xl mx-auto px-6 py-24 text-center"
          style={{ background: "linear-gradient(180deg, #FAF7F1, #FFFFFF)" }}
        >
          <CheckCircle2 size={48} className="mx-auto text-[var(--ff-primary)]" />
          <h1 className="mt-6 font-display text-4xl text-[var(--ff-text-primary)]">
            Application Received
          </h1>
          <p className="mt-4 text-[var(--ff-text-secondary)] leading-relaxed max-w-xl mx-auto">
            Thank you for applying to become a Brand Partner with The Farm To
            You. Our team will review your application and reach out if we
            believe there is a strong fit for a collaboration.
          </p>
          {done.application_id && (
            <p className="mt-4 text-xs text-[var(--ff-text-muted)] font-mono">
              Reference: {done.application_id}
            </p>
          )}
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/" className="btn-secondary">
              Back to home
            </Link>
            <button
              type="button"
              onClick={() => navigate(0)}
              className="text-sm text-[var(--ff-primary)] underline"
            >
              Submit another application
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="brand-apply-page">
      <Seo
        title="Become a Brand Partner"
        description="Partner with The Farm To You to share curated farm-to-door boxes, limited drops, trusted sourcing, and better food with your audience."
        path="/partners/brand"
      />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, #FAF7F1 0%, #F3EFE6 60%, #FFFFFF 100%)",
        }}
      >
        <div
          aria-hidden
          className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(circle, #F4A26155, transparent 65%)" }}
        />
        <div className="max-w-4xl mx-auto px-6 py-20 lg:py-24 relative">
          <p className="overline text-[var(--ff-secondary)]">Partner with us</p>
          <h1
            className="mt-3 font-display leading-[1.05] text-[var(--ff-text-primary)]"
            style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)" }}
          >
            Become a Brand Partner
          </h1>
          <p className="mt-5 text-lg text-[var(--ff-text-secondary)] leading-relaxed max-w-2xl">
            Partner with The Farm To You to help share curated farm-to-door
            boxes, limited drops, trusted sourcing, and better food with your
            audience.
          </p>
          <p className="mt-5 text-[var(--ff-text-secondary)] leading-relaxed max-w-2xl">
            The Farm To You works with creators, influencers, athletes, chefs,
            pageant queens, personal trainers, restaurants, franchises, and
            community leaders who align with our mission. If you love
            high-quality food, cooking with confidence, supporting farmers, and
            sharing products you believe in, we would love to hear from you.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-12">
        {/* Form */}
        <form
          onSubmit={submit}
          className="lg:col-span-8 space-y-8"
          data-testid="brand-apply-form"
        >
          {/* Honeypot — opaque name so autofill never targets it */}
          <input
            type="text"
            value={form.hp_token}
            onChange={(e) => update("hp_token", e.target.value)}
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
            autoComplete="off"
            name="hp_token"
          />

          <Section title="About You">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name *" htmlFor="full_name">
                <input
                  id="full_name"
                  required
                  className="input-base w-full"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  data-testid="brand-full-name"
                />
              </Field>
              <Field label="Email Address *" htmlFor="email">
                <input
                  id="email"
                  type="email"
                  required
                  className="input-base w-full"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  data-testid="brand-email"
                />
              </Field>
              <Field label="Phone Number" htmlFor="phone">
                <input
                  id="phone"
                  className="input-base w-full"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  data-testid="brand-phone"
                />
              </Field>
              <Field label="City / State *" htmlFor="city_state">
                <input
                  id="city_state"
                  required
                  placeholder="Austin, TX"
                  className="input-base w-full"
                  value={form.city_state}
                  onChange={(e) => update("city_state", e.target.value)}
                  data-testid="brand-city-state"
                />
              </Field>
            </div>
          </Section>

          <Section title="Where You Create">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Instagram Handle" htmlFor="instagram" icon={Instagram}>
                <input
                  id="instagram"
                  placeholder="@yourhandle"
                  className="input-base w-full"
                  value={form.instagram}
                  onChange={(e) => update("instagram", e.target.value)}
                  data-testid="brand-instagram"
                />
              </Field>
              <Field label="TikTok Handle" htmlFor="tiktok" icon={Music2}>
                <input
                  id="tiktok"
                  placeholder="@yourhandle"
                  className="input-base w-full"
                  value={form.tiktok}
                  onChange={(e) => update("tiktok", e.target.value)}
                  data-testid="brand-tiktok"
                />
              </Field>
              <Field label="YouTube Channel" htmlFor="youtube" icon={Youtube}>
                <input
                  id="youtube"
                  placeholder="youtube.com/@yourchannel"
                  className="input-base w-full"
                  value={form.youtube}
                  onChange={(e) => update("youtube", e.target.value)}
                  data-testid="brand-youtube"
                />
              </Field>
              <Field label="Website / Media Kit Link" htmlFor="website" icon={Globe}>
                <input
                  id="website"
                  placeholder="https://"
                  className="input-base w-full"
                  value={form.website}
                  onChange={(e) => update("website", e.target.value)}
                  data-testid="brand-website"
                />
              </Field>
            </div>
          </Section>

          <Section title="Your Website Feature Card">
            <p className="text-sm text-[var(--ff-text-secondary)] mb-4 -mt-2">
              If you're approved as an ambassador, we'll feature you on our site next to our other
              tastemakers. This info builds your card — please add a great photo and keep it authentic.
            </p>
            <div className="space-y-4">
              <Field label="Profile Photo *" htmlFor="photo">
                <div className="flex items-center gap-4">
                  {form.photo_url ? (
                    <img src={form.photo_url} alt="Your profile" className="w-20 h-20 rounded-xl object-cover border border-[var(--ff-border)]" data-testid="brand-photo-preview" />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-[var(--ff-muted)] flex items-center justify-center text-[var(--ff-text-muted)]">
                      <Camera size={22} />
                    </div>
                  )}
                  <div>
                    <input id="photo" type="file" accept="image/*" onChange={(e) => uploadPhoto(e.target.files?.[0])} className="text-sm" data-testid="brand-photo-input" />
                    {photoUploading && <p className="text-xs text-[var(--ff-text-muted)] mt-1">Uploading…</p>}
                    {photoErr && <p className="text-xs text-rose-700 mt-1" data-testid="brand-photo-error">{photoErr}</p>}
                    <p className="text-[11px] text-[var(--ff-text-muted)] mt-1">A clear headshot or lifestyle photo. JPG/PNG, under 8 MB.</p>
                  </div>
                </div>
              </Field>
              <Field label="Title / Tagline *" htmlFor="title_tagline">
                <input
                  id="title_tagline"
                  required
                  placeholder="e.g. Bodybuilder & Fitness Creator"
                  className="input-base w-full"
                  value={form.title_tagline}
                  onChange={(e) => update("title_tagline", e.target.value)}
                  data-testid="brand-title-tagline"
                />
              </Field>
              <Field label="Short Public Bio *" htmlFor="public_bio">
                <textarea
                  id="public_bio"
                  required
                  rows={3}
                  maxLength={600}
                  placeholder="1–2 sentences we can show on your card — who you are and why you love real, farm-raised food."
                  className="input-base w-full"
                  value={form.public_bio}
                  onChange={(e) => update("public_bio", e.target.value)}
                  data-testid="brand-public-bio"
                />
              </Field>
              <Field label="Featured Quote *" htmlFor="featured_quote">
                <textarea
                  id="featured_quote"
                  required
                  rows={2}
                  maxLength={400}
                  placeholder="A short pull-quote in your own words about The Farm To You."
                  className="input-base w-full"
                  value={form.featured_quote}
                  onChange={(e) => update("featured_quote", e.target.value)}
                  data-testid="brand-featured-quote"
                />
              </Field>
            </div>
          </Section>

          <Section title="Your Partnership Profile">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Type of Partner *" htmlFor="partner_type">
                <select
                  id="partner_type"
                  required
                  className="input-base w-full"
                  value={form.partner_type}
                  onChange={(e) => update("partner_type", e.target.value)}
                  data-testid="brand-type"
                >
                  {PARTNER_TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Approximate Follower Count" htmlFor="follower_count">
                <input
                  id="follower_count"
                  placeholder="e.g. 12k IG · 40k TikTok"
                  className="input-base w-full"
                  value={form.follower_count}
                  onChange={(e) => update("follower_count", e.target.value)}
                  data-testid="brand-followers"
                />
              </Field>
            </div>
            <Field label="Primary Audience" htmlFor="primary_audience">
              <input
                id="primary_audience"
                placeholder="Busy moms · fitness pros · wellness families · etc."
                className="input-base w-full"
                value={form.primary_audience}
                onChange={(e) => update("primary_audience", e.target.value)}
                data-testid="brand-audience"
              />
            </Field>
            <Field
              label="Why do you want to partner with The Farm To You? *"
              htmlFor="why_partner"
            >
              <textarea
                id="why_partner"
                required
                rows={4}
                minLength={20}
                maxLength={4000}
                className="input-base w-full resize-y"
                placeholder="Tell us about your audience, content style, and why our farm-to-door mission resonates with you."
                value={form.why_partner}
                onChange={(e) => update("why_partner", e.target.value)}
                data-testid="brand-why"
              />
            </Field>
            <Field
              label="Have you worked with food, wellness, fitness, or lifestyle brands before?"
              htmlFor="prior"
            >
              <textarea
                id="prior"
                rows={3}
                maxLength={2000}
                className="input-base w-full resize-y"
                placeholder="Optional — share previous brand collaborations, campaigns, or partnerships."
                value={form.prior_brand_experience}
                onChange={(e) => update("prior_brand_experience", e.target.value)}
                data-testid="brand-experience"
              />
            </Field>
            <div>
              <p className="text-sm font-semibold text-[var(--ff-text-primary)] mb-2">
                What are you interested in?
              </p>
              <div className="flex flex-wrap gap-2" data-testid="brand-interests">
                {COLLAB_INTERESTS.map((label) => {
                  const on = form.collab_interests.includes(label);
                  return (
                    <button
                      type="button"
                      key={label}
                      onClick={() => toggleInterest(label)}
                      className={`px-4 py-2 rounded-full text-sm transition-all border ${
                        on
                          ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]"
                          : "bg-white text-[var(--ff-text-secondary)] border-[var(--ff-border)] hover:border-[var(--ff-primary)]"
                      }`}
                      data-testid={`brand-interest-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <Field label="Upload Media Kit (link)" htmlFor="media_kit_url">
              <input
                id="media_kit_url"
                placeholder="https:// (Drive, Notion, Dropbox)"
                className="input-base w-full"
                value={form.media_kit_url}
                onChange={(e) => update("media_kit_url", e.target.value)}
                data-testid="brand-media-kit"
              />
            </Field>
            <Field label="Additional Notes" htmlFor="additional_notes">
              <textarea
                id="additional_notes"
                rows={3}
                maxLength={2000}
                className="input-base w-full resize-y"
                placeholder="Anything else we should know?"
                value={form.additional_notes}
                onChange={(e) => update("additional_notes", e.target.value)}
                data-testid="brand-notes"
              />
            </Field>
          </Section>

          {err && (
            <p className="text-sm text-rose-700" data-testid="brand-err">
              {err}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="brand-submit"
            >
              <Send size={14} />
              {submitting ? "Submitting…" : "Submit Application"}
            </button>
            <p className="text-xs text-[var(--ff-text-muted)] italic">
              By submitting, you agree to be contacted about potential
              partnerships. We do not sell or share applicant data.
            </p>
          </div>
        </form>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-6">
          <div
            className="rounded-2xl p-6 border border-[var(--ff-border)]"
            style={{ background: "#FAF7F1" }}
          >
            <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--ff-secondary)]">
              <Sparkles size={12} /> What to expect
            </div>
            <ul className="mt-4 space-y-3 text-sm text-[var(--ff-text-secondary)]">
              <li>· A real human reviews every application</li>
              <li>· We respond to qualifying applicants within 7–14 days</li>
              <li>· Partnerships range from gifted product to long-term ambassadorships</li>
              <li>· All paid or gifted collaborations are disclosed per FTC guidelines</li>
            </ul>
          </div>

          <div
            className="rounded-2xl p-6 border border-[var(--ff-border)] bg-white"
            data-testid="brand-farm-partner-callout"
          >
            <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-[var(--ff-secondary)]">
              <Sprout size={12} /> Farm or food partner?
            </div>
            <h3 className="mt-2 font-display text-xl text-[var(--ff-text-primary)]">
              Sell Through The Farm To You
            </h3>
            <p className="mt-2 text-sm text-[var(--ff-text-secondary)] leading-relaxed">
              Are you a rancher, fisherman, seafood supplier, livestock farmer,
              or specialty food partner interested in selling through The Farm
              To You? Apply to become a partner.
            </p>
            <Link
              to="/partners/apply"
              data-testid="brand-go-farm-apply"
              className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--ff-primary)] underline font-semibold"
            >
              Apply as a Ranch, Seafood, or Food Partner <ArrowRight size={14} />
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-4">
      <h2 className="font-display text-2xl text-[var(--ff-text-primary)]">
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, htmlFor, icon: Icon, children }) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="text-xs uppercase tracking-wider text-[var(--ff-text-muted)] mb-1.5 inline-flex items-center gap-1.5"
      >
        {Icon && <Icon size={12} />}
        {label}
      </label>
      {children}
    </div>
  );
}
